/**
 * The shared vocabulary every status map is written in.
 *
 * These lived in `core/applications/application.types.ts` until FLOW-207, beside
 * the generic-loan `ApplicationStatus` enum that predates Kickstart Cash and
 * Debtors Finance. That enum is gone; these are not, so they moved somewhere
 * named after what they are rather than after a retired product.
 *
 * The concrete maps stay with their products — `KS_STATUS_META` in
 * `core/kickstart`, `DF_*_STATUS_META` in `core/df`, `ENTITY_STATUS_META` in
 * `core/entity`, `PRE_REVIEW_STATUS_META` in `core/applications`. Only the
 * shape they share lives here, which is why this module imports nothing.
 */

/**
 * Chip colour roles. Variants mirror `docs/frontend/styling/capx-roles.md`
 * (status → badge variant); colour is always paired with the text label, never
 * used alone (CapX accessibility rule).
 */
export type BadgeVariant = 'neutral' | 'default' | 'warning' | 'success' | 'destructive';

export interface StatusMeta {
  label: string;
  variant: BadgeVariant;
}

/**
 * AI advisory recommendation enums (Ops-only) — the advisory verdict surfaced
 * as a chip in the AI Advisory panel, not a lifecycle status of anything.
 */
export const AI_RECOMMENDATION_META: Record<string, StatusMeta> = {
  APPROVE: { label: 'Approve', variant: 'success' },
  MORE_INFO: { label: 'More info needed', variant: 'warning' },
  DECLINE: { label: 'Decline', variant: 'destructive' },
};

/**
 * The one label for a denormalised AI recommendation enum mirror (Ops queues,
 * worklists, dashboards). Reads `AI_RECOMMENDATION_META` so the text can never
 * drift from the chip's; `—` for none, the raw code for one not yet mapped.
 */
export function aiRecommendationLabel(rec: string | null | undefined): string {
  return rec ? (AI_RECOMMENDATION_META[rec]?.label ?? rec) : '—';
}
